import { CellContext } from '@tanstack/react-table';
import { getTableMeta, UnallocatedCampaignsRow } from '../../helpers';
import { Skeleton } from '@round/ui-kit';
import { Select } from '@round/whitelabel-ui';
import { StylesConfig } from 'react-select';
import FinanceStatusBadgeOption from './FinanceStatusBadge/FinanceStatusBadgeOption';
import FinanceStatusBadgeValue from './FinanceStatusBadge/FinanceStatusBadgeValue';
import { GenericDropdownOption } from 'App.types';
import { creatorbase } from '@round/api';
import { showNotification } from 'helpers';
import FinanceStatusBadge from './FinanceStatusBadge/FinanceStatusBadge';
import { financeStatusOptions } from './helpers';
import { useInvalidateInfiniteTeams, useUpdateCampaign } from '@round/creatorbase-helpers/api-hooks';

type Context = CellContext<UnallocatedCampaignsRow, UnallocatedCampaignsRow['finance_status']>;

const FinanceStatusCell = ({ row, table, getValue }: Context) => {
    const value = getValue();
    const { isLoading, isRefetching, onUpdated } = getTableMeta(table);
    const { update, isLoading: isUpdateLoading } = useUpdateCampaign(row.original.id);
    const invalidateTeams = useInvalidateInfiniteTeams();

    const handleUpdateCampaign = (updates: Partial<creatorbase.PatchCampaignBody>) => {
        update(
            { id: row.original.id, ...updates },
            {
                onSuccess: (data) => {
                    onUpdated(data);
                    invalidateTeams({ include_credit_breakdown: true }, { type: 'active' });
                },
                onError: () => {
                    showNotification('Error updating finance status', 'error');
                },
            }
        );
    };

    if (isLoading || Object.keys(row.original).length === 0) {
        return <Skeleton width="9rem" />;
    }

    const financeStatusSelectStyles: StylesConfig = {
        container: (base) => ({
            ...base,
            width: 'fit-content',
            minWidth: '9rem',
        }),
        control: (base) => ({
            ...base,
            height: 'auto',
            border: 'none',
            padding: 0,
            justifyContent: 'start',
            gap: 0,
            '&:hover': {
                borderColor: 'red !important',
                padding: '0 !important',
            },
        }),
        valueContainer: (base) => ({
            ...base,
            maxWidth: '8.5rem',
            flexShrink: 0,
            flexWrap: 'nowrap',
            gap: 0,
        }),
        option: (base) => ({
            ...base,
            padding: '0.25rem',
        }),
        singleValue: () => ({
            padding: 0,
        }),
    };

    if (value === 'invoiced') {
        return <FinanceStatusBadge financeStatus={value} />;
    }

    return (
        <Select
            isLoading={isUpdateLoading || isLoading}
            isDisabled={isRefetching}
            options={financeStatusOptions.filter((o) => o.value !== 'invoiced')}
            value={financeStatusOptions.find((o) => o.value === value)}
            onChange={(option) => {
                const selectedOption = option as GenericDropdownOption<creatorbase.FinanceStatus>;
                if (!selectedOption) {
                    return;
                }

                handleUpdateCampaign({ finance_status: selectedOption.value });
            }}
            placeholder="Select status..."
            styles={financeStatusSelectStyles}
            isSearchable={false}
            components={{ Option: FinanceStatusBadgeOption, SingleValue: FinanceStatusBadgeValue }}
        />
    );
};

export default FinanceStatusCell;
